<template lang="pug">
  .youtube-meet-embed
    .youtube-meet-embed__loading(v-if="loading") 讀取中...
    VideoPlayer.youtube-meet-embed__hls(
      v-else-if="hlsUrl",
      :autoplay="true",
      :source="hlsUrl",
      :course_id="courseId",
      course_type="course"
    )
    .youtube-meet-embed__empty(v-else-if="!videoId")
      | 直播尚未開始 — 請稍候系統建立直播訊號
      a(href="javascript:void(0)", @click="reload")  重新整理
    .youtube-meet-embed__player(v-else)
      iframe(
        :src="iframeSrc",
        frameborder="0",
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture",
        allowfullscreen
      )
      .youtube-meet-embed__overlay-tip(v-if="showTip")
        | 直播由 GVO 地球村頻道提供 ｜
        span 學員不需要 YouTube 帳號即可觀看
</template>

<script>
import meetingMixin from './mixins/meeting'
import VideoPlayer from '@/components/video.vue'

/**
 * 團體課直播嵌入元件。
 *
 * 對應計畫 §4.5（仿 course-apis/web-pack 同名元件移植）
 *
 * 使用：
 *   <youtube-meet-embed :course-id="courseId" />
 *
 * 流程：
 *   1. 透過 meeting mixin 的 fetchEmbedInfo() 拿播放資訊
 *   2. 優先播放自建 HLS hlsUrl
 *   3. 沒有 hlsUrl 時 fallback 到 YouTube iframe
 */
export default {
  name: 'YoutubeMeetEmbed',
  mixins: [meetingMixin],
  components: {
    VideoPlayer
  },
  props: {
    courseId: { type: [String, Number], required: true },
    autoReload: { type: Boolean, default: false },
    showTip: { type: Boolean, default: true }
  },
  data () {
    return {
      loading: false,
      hlsUrl: null,
      videoId: null,
      provider: null,
      isLive: false,
      scheduledStart: null,
      reloadTimer: null
    }
  },
  computed: {
    iframeSrc () {
      if (!this.videoId) return ''
      const params = [
        'modestbranding=1',
        'rel=0',
        'controls=1',
        'disablekb=1',
        'autoplay=1'
      ].join('&')
      return 'https://www.youtube.com/embed/' + encodeURIComponent(this.videoId) + '?' + params
    }
  },
  mounted () {
    this.reload()
    // 自動重新整理：若 isLive=false 但已過排定開始時間，每 30 秒重抓一次直到 videoId 有
    if (this.autoReload) {
      this.reloadTimer = setInterval(() => {
        if ((!this.hlsUrl && !this.videoId) || !this.isLive) {
          this.reload()
        }
      }, 30000)
    }
  },
  beforeDestroy () {
    if (this.reloadTimer) clearInterval(this.reloadTimer)
  },
  methods: {
    reload () {
      this.loading = true
      this.fetchEmbedInfo(this.courseId).then((data) => {
        this.hlsUrl = data && data.hlsUrl ? data.hlsUrl : null
        this.videoId = data && data.youtubeVideoId ? data.youtubeVideoId : null
        this.provider = data && data.provider ? data.provider : null
        this.isLive = !!(data && data.isLive)
        this.scheduledStart = (data && data.scheduledStart) || null
        this.loading = false
      }).catch((err) => {
        console.error('[youtube-meet-embed] fetchEmbedInfo failed', err)
        this.loading = false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.youtube-meet-embed {
  width: 100%;
  position: relative;

  &__loading,
  &__empty {
    text-align: center;
    padding: 40px 20px;
    color: #888;
    background: #f5f5f5;
    border-radius: 6px;
  }

  &__empty a { margin-left: 8px; }

  &__player {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    background: #000;
    border-radius: 6px;
    overflow: hidden;

    iframe {
      width: 100%;
      height: 100%;
      border: 0;
    }
  }

  &__overlay-tip {
    position: absolute;
    bottom: 8px;
    left: 8px;
    right: 8px;
    background: rgba(0, 0, 0, 0.55);
    color: #fff;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    pointer-events: none;
  }
}
</style>
